import React, { useEffect, useState, useCallback } from 'react';
import { FiEdit, FiTrash2, FiPlus, FiAlertTriangle,
         FiCheckCircle, FiSearch } from 'react-icons/fi';
import { AdminSidebar } from '../../components/admin';
import { Modal } from '../../components/ui';

const API_BASE = 'http://localhost:8080';

const CATEGORIES = [
  { value: 'network',   label: 'Réseaux & IT',   color: '#1d4ed8', bg: '#eff6ff' },
  { value: 'metalwork', label: 'Métallurgie',     color: '#15803d', bg: '#f0fdf4' },
  { value: 'software',  label: 'Développement',  color: '#7e22ce', bg: '#faf5ff' },
];

const AdminPortfolios = () => {
  const [portfolios, setPortfolios]       = useState([]);
  const [loading, setLoading]             = useState(true);
  const [showFormModal, setShowFormModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [modalType, setModalType]         = useState('add');
  const [selected, setSelected]           = useState(null);
  const [form, setForm]                   = useState({
    title: '', category: 'network', description: '', technologies: '', image: ''
  });
  const [notification, setNotification]   = useState({ show: false, type: '', msg: '' });
  const [searchTerm, setSearchTerm]       = useState('');
  const [filterCat, setFilterCat]         = useState('all');
  const [modalLoading, setModalLoading]   = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);

  const token = localStorage.getItem('token');

  // ─── Fetch ───────────────────────────────────────────────
  const fetchPortfolios = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/portfolios`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setPortfolios(Array.isArray(data) ? data : []);
    } catch {
      showNotify('error', 'Erreur de chargement');
    }
    setLoading(false);
  }, [token]);

  useEffect(() => { fetchPortfolios(); }, [fetchPortfolios]);

  // ─── Filtrage local ───────────────────────────────────────
  const displayed = portfolios.filter(p => {
    const matchCat    = filterCat === 'all' || p.category === filterCat;
    const matchSearch = !searchTerm ||
      p.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.description?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchCat && matchSearch;
  });

  // ─── Helpers ──────────────────────────────────────────────
  const showNotify = (type, msg) => {
    setNotification({ show: true, type, msg });
    setTimeout(() => setNotification({ show: false, type: '', msg: '' }), 3000);
  };

  const handleShowForm = (type, item = null) => {
    setModalType(type);
    setSelected(item);
    setForm(item ? {
      title:        item.title,
      category:     item.category,
      description:  item.description || '',
      technologies: Array.isArray(item.technologies)
                      ? item.technologies.join(', ') : '',
      image:        item.image || ''
    } : { title: '', category: 'network', description: '', technologies: '', image: '' });
    setShowFormModal(true);
  };

  // ─── Upload image ─────────────────────────────────────────
  const handleFileChange = async e => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadLoading(true);
    const fd = new FormData();
    fd.append('file', file);
    fd.append('type', 'portfolios');
    try {
      const res = await fetch(`${API_BASE}/api/files/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: fd
      });
      const path = await res.text();
      setForm(prev => ({ ...prev, image: path }));
      showNotify('success', 'Image téléchargée !');
    } catch {
      showNotify('error', "Échec de l'upload");
    } finally {
      setUploadLoading(false);
    }
  };

  // ─── Save ─────────────────────────────────────────────────
  const handleSubmit = async e => {
    e.preventDefault();
    setModalLoading(true);
    const payload = {
      ...form,
      technologies: form.technologies
        .split(',').map(t => t.trim()).filter(Boolean)
    };
    try {
      const method = modalType === 'add' ? 'POST' : 'PUT';
      const url    = modalType === 'add'
        ? `${API_BASE}/api/portfolios`
        : `${API_BASE}/api/portfolios/${selected.id}`;
      const res = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });
      if (!res.ok) throw new Error();
      showNotify('success',
        modalType === 'add' ? 'Projet ajouté !' : 'Projet mis à jour !');
      fetchPortfolios();
      setShowFormModal(false);
    } catch {
      showNotify('error', "Erreur lors de l'enregistrement");
    } finally {
      setModalLoading(false);
    }
  };

  // ─── Delete ───────────────────────────────────────────────
  const executeDelete = async () => {
    setModalLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/portfolios/${selected.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error();
      showNotify('success', 'Projet supprimé !');
      fetchPortfolios();
      setShowDeleteModal(false);
    } catch {
      showNotify('error', 'Erreur lors de la suppression');
    } finally {
      setModalLoading(false);
    }
  };

  return (
    <div className="admin-container">
      <AdminSidebar />
      <div className="admin-content">

        {/* Header */}
        <div className="admin-header">
          <div>
            <h2>Gestion des Portfolios</h2>
            <p className="text-muted small">
              {portfolios.length} projet(s) — {displayed.length} affiché(s)
            </p>
          </div>

          <div className="search-wrapper">
            <FiSearch className="search-icon" />
            <input
              type="text"
              placeholder="Rechercher un projet..."
              className="search-input"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
          </div>

          <select
            className="cat-filter"
            value={filterCat}
            onChange={e => setFilterCat(e.target.value)}
          >
            <option value="all">Toutes catégories</option>
            {CATEGORIES.map(c => (
              <option key={c.value} value={c.value}>{c.label}</option>
            ))}
          </select>

          <button onClick={() => handleShowForm('add')} className="btn-add">
            <FiPlus /> Nouveau Projet
          </button>
        </div>

        {/* Toast */}
        {notification.show && (
          <div className={`toast-sii ${notification.type}`}>
            {notification.type === 'success'
              ? <FiCheckCircle /> : <FiAlertTriangle />}
            {notification.msg}
          </div>
        )}

        {/* Table */}
        {loading ? (
          <div className="loader-container"><div className="loader" /></div>
        ) : (
          <table className="admin-table">
            <thead>
              <tr>
                <th>Image</th>
                <th>Titre</th>
                <th>Catégorie</th>
                <th>Technologies</th>
                <th className="text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              {displayed.length > 0 ? displayed.map(p => {
                const cat = CATEGORIES.find(c => c.value === p.category);
                return (
                  <tr key={p.id}>
                    <td>
                      {p.image ? (
                        <img
                          src={p.image.startsWith('http')
                            ? p.image : `${API_BASE}${p.image}`}
                          alt={p.title}
                          className="admin-logo-img"
                          onError={e => { e.target.style.display = 'none'; }}
                        />
                      ) : (
                        <div className="img-placeholder">📁</div>
                      )}
                    </td>
                    <td>
                      <div className="fw-bold">{p.title}</div>
                      <small className="text-muted">
                        {p.description?.slice(0, 60)}
                        {p.description?.length > 60 ? '…' : ''}
                      </small>
                    </td>
                    <td>
                      {cat && (
                        <span className="cat-badge"
                          style={{ background: cat.bg, color: cat.color }}>
                          {cat.label}
                        </span>
                      )}
                    </td>
                    <td>
                      <div className="tech-list">
                        {(p.technologies || []).map((t, i) => (
                          <span key={i} className="tech-tag">{t}</span>
                        ))}
                      </div>
                    </td>
                    <td className="text-end">
                      <button
                        onClick={() => handleShowForm('edit', p)}
                        className="btn-icon edit">
                        <FiEdit />
                      </button>
                      <button
                        onClick={() => { setSelected(p); setShowDeleteModal(true); }}
                        className="btn-icon delete">
                        <FiTrash2 />
                      </button>
                    </td>
                  </tr>
                );
              }) : (
                <tr>
                  <td colSpan={5} className="text-center py-4">
                    Aucun projet trouvé.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}

        {/* Modal Formulaire */}
        <Modal
          isOpen={showFormModal}
          onClose={() => setShowFormModal(false)}
          title={modalType === 'add' ? 'Ajouter un projet' : 'Modifier le projet'}
        >
          <form onSubmit={handleSubmit} className="admin-form">
            <input
              value={form.title}
              onChange={e => setForm({ ...form, title: e.target.value })}
              placeholder="Titre du projet"
              required
              className="form-input"
            />
            <select
              value={form.category}
              onChange={e => setForm({ ...form, category: e.target.value })}
              className="form-input"
            >
              {CATEGORIES.map(c => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
            <textarea
              value={form.description}
              onChange={e => setForm({ ...form, description: e.target.value })}
              placeholder="Description du projet..."
              className="form-input"
              rows="3"
            />
            <div className="upload-box">
              <label>Technologies (séparées par des virgules) :</label>
              <input
                value={form.technologies}
                onChange={e => setForm({ ...form, technologies: e.target.value })}
                placeholder="React, Node.js, MySQL"
                className="form-input"
              />
            </div>
            <div className="upload-box">
              <label>Image du projet :</label>
              <input type="file" onChange={handleFileChange} accept="image/*" />
            </div>
            {form.image && (
              <div className="preview-container">
                <img
                  src={form.image.startsWith('http')
                    ? form.image : `${API_BASE}${form.image}`}
                  alt="preview"
                  className="preview-img"
                  onError={e => { e.target.style.display = 'none'; }}
                />
              </div>
            )}
            <button type="submit" className="btn-save"
              disabled={modalLoading || uploadLoading}>
              {modalLoading ? 'Sauvegarde...' : 'Confirmer'}
            </button>
          </form>
        </Modal>

        {/* Modal Suppression */}
        <Modal
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          title="Confirmation de suppression"
        >
          <div className="text-center p-3">
            <FiAlertTriangle size={50} color="#ef4444" />
            <h4 className="mt-3">Suppression définitive ?</h4>
            <p>Voulez-vous supprimer <b>{selected?.title}</b> ?</p>
            <div className="d-flex justify-content-center gap-3 mt-4">
              <button className="btn-cancel"
                onClick={() => setShowDeleteModal(false)}>
                Annuler
              </button>
              <button className="btn-confirm-delete"
                onClick={executeDelete} disabled={modalLoading}>
                Oui, supprimer
              </button>
            </div>
          </div>
        </Modal>

      </div>

      <style>{`
        .admin-container { display:flex; min-height:100vh; background:#f4f7fe; }
        .admin-content { flex:1; padding:40px; position:relative; }
        .admin-header { display:flex; justify-content:space-between; align-items:center; margin-bottom:30px; gap:20px; flex-wrap:wrap; }
        .btn-add { background:#d90000; color:white; border:none; padding:12px 24px; border-radius:10px; cursor:pointer; font-weight:600; display:flex; align-items:center; gap:8px; white-space:nowrap; }
        .search-wrapper { position:relative; flex:1; max-width:350px; }
        .search-icon { position:absolute; left:15px; top:50%; transform:translateY(-50%); color:#94a3b8; }
        .search-input { width:100%; padding:12px 12px 12px 45px; border-radius:25px; border:1px solid #e2e8f0; outline:none; transition:0.3s; }
        .search-input:focus { border-color:#d90000; box-shadow:0 0 0 4px rgba(217,0,0,0.1); }
        .cat-filter { padding:10px 14px; border-radius:10px; border:1px solid #e2e8f0; outline:none; background:white; cursor:pointer; }
        .admin-table { width:100%; background:white; border-radius:12px; border-collapse:collapse; box-shadow:0 4px 12px rgba(0,0,0,0.05); }
        .admin-table td, .admin-table th { padding:15px; border-bottom:1px solid #f1f5f9; }
        .admin-logo-img { height:45px; width:70px; object-fit:cover; border-radius:8px; }
        .img-placeholder { width:70px; height:45px; background:#f1f5f9; border-radius:8px; display:flex; align-items:center; justify-content:center; font-size:20px; }
        .cat-badge { padding:4px 12px; border-radius:20px; font-size:12px; font-weight:600; }
        .tech-list { display:flex; flex-wrap:wrap; gap:4px; }
        .tech-tag { background:#f1f5f9; color:#475569; padding:2px 8px; border-radius:10px; font-size:11px; font-weight:500; }
        .btn-icon { background:none; border:none; cursor:pointer; font-size:18px; margin-left:10px; padding:8px; border-radius:8px; transition:0.2s; }
        .edit { color:#3b82f6; } .edit:hover { background:#eff6ff; }
        .delete { color:#ef4444; } .delete:hover { background:#fef2f2; }
        .admin-form { display:flex; flex-direction:column; gap:15px; }
        .form-input { padding:12px; border:1px solid #e2e8f0; border-radius:10px; outline:none; width:100%; font-family:inherit; }
        .upload-box { display:flex; flex-direction:column; gap:6px; }
        .upload-box label { font-size:13px; font-weight:600; color:#475569; }
        .preview-container { text-align:center; background:#f8fafc; padding:10px; border-radius:10px; }
        .preview-img { height:80px; object-fit:cover; border-radius:8px; }
        .btn-save { background:#d90000; color:white; border:none; padding:15px; border-radius:10px; cursor:pointer; font-weight:700; }
        .btn-cancel { background:#e2e8f0; border:none; padding:10px 20px; border-radius:8px; cursor:pointer; font-weight:600; }
        .btn-confirm-delete { background:#ef4444; color:white; border:none; padding:10px 20px; border-radius:8px; cursor:pointer; font-weight:600; }
        .toast-sii { position:fixed; top:20px; right:20px; padding:15px 25px; border-radius:12px; color:white; z-index:9999; display:flex; align-items:center; gap:10px; font-weight:600; box-shadow:0 10px 20px rgba(0,0,0,0.1); }
        .toast-sii.success { background:#10b981; } .toast-sii.error { background:#ef4444; }
        .loader { width:40px; height:40px; border:4px solid #f1f5f9; border-top:4px solid #d90000; border-radius:50%; animation:spin 1s linear infinite; }
        @keyframes spin { to { transform:rotate(360deg); } }
        .loader-container { display:flex; justify-content:center; padding:100px; }
      `}</style>
    </div>
  );
};

export default AdminPortfolios;